import { BelegAktion, BelegKarte, BelegKarteTyp } from '../types';

/** Reihenfolge, in der eine auslösende Aktion zur Primär-Schaltfläche wird. */
const PRIMAER_REIHENFOLGE: BelegAktion[] = ['senden', 'buchen', 'genehmigen', 'vorlegen'];

/** Beschriftung einer Karten-Aktion (je Belegart, wo es einen Unterschied macht). */
export function aktionLabel(a: BelegAktion, typ: BelegKarteTyp): string {
  switch (a) {
    case 'senden': return 'Rechnung senden';
    case 'vorlegen': return 'Zur Prüfung vorlegen';
    case 'ansehen': return 'Ansehen';
    case 'bearbeiten': return 'Bearbeiten';
    case 'zum_auftrag': return 'Zum Auftrag';
    case 'zum_kunden': return 'Zum Kunden';
    case 'buchen': return typ === 'lieferschein' ? 'Buchen und drucken' : 'Zahlung buchen';
    case 'ansehen_rechnung': return 'Rechnung ansehen';
    case 'verwerfen': return 'Verwerfen';
    case 'genehmigen': return 'Genehmigen';
    case 'ablehnen': return 'Ablehnen';
  }
}

/** Erste auslösende Aktion der Karte (senden, buchen, genehmigen, vorlegen) oder null. */
export function primaereAktion(k: BelegKarte): BelegAktion | null {
  return PRIMAER_REIHENFOLGE.find(a => k.aktionen.includes(a)) ?? null;
}

function text(wert: unknown): string | null {
  return typeof wert === 'string' && wert.trim() ? wert : null;
}

/**
 * Optimistischer Folgezustand der Karte nach erfolgreicher Aktion (bis zum nächsten
 * Laden der Konversation, das die Karte serverseitig frisch auflöst). Reine Funktion.
 */
export function nachAktion(k: BelegKarte, a: BelegAktion, antwort: Record<string, unknown>): BelegKarte {
  switch (a) {
    case 'senden': {
      const nummer = text(antwort.rechnungsnummer) ?? k.nummer;
      return {
        ...k,
        status: 'VERSENDET',
        nummer,
        titel: k.titel.includes(k.nummer) ? k.titel.replace(k.nummer, nummer) : k.titel,
        empfaenger: text(antwort.empfaenger) ?? k.empfaenger,
        auftrag_status: text(antwort.auftrag_status) ?? k.auftrag_status,
        aktionen: ['ansehen', 'zum_auftrag', 'zum_kunden'],
        freigabe_id: null,
      };
    }
    case 'buchen':
      if (k.typ === 'lieferschein') {
        return { ...k, status: 'VERSENDET', aktionen: ['zum_auftrag'] };
      }
      return { ...k, status: 'GENEHMIGT', aktionen: ['ansehen_rechnung'] };
    case 'genehmigen':
      return { ...k, status: 'GENEHMIGT', aktionen: ['ansehen_rechnung'] };
    case 'verwerfen':
    case 'ablehnen':
      return { ...k, status: 'ABGELEHNT', aktionen: [] };
    case 'vorlegen':
      return { ...k, aktionen: k.aktionen.filter(x => x !== 'vorlegen') };
    default:
      return k;
  }
}

/** Anzeigenummer der Karte (Fallback `#id`). */
export function nummerAnzeige(k: BelegKarte): string {
  return k.nummer && k.nummer.trim() ? k.nummer : `#${k.id}`;
}

const STATUS_LABEL: Record<string, string> = {
  ENTWURF: 'Entwurf',
  VERSENDET: 'Versendet',
  OFFEN: 'Offen',
  GENEHMIGT: 'Genehmigt',
  ABGELEHNT: 'Abgelehnt',
  FEHLGESCHLAGEN: 'Fehlgeschlagen',
  GELOESCHT: 'Gelöscht',
  BEZAHLT: 'Bezahlt',
  TEILBEZAHLT: 'Teilweise bezahlt',
  UEBERFAELLIG: 'Überfällig',
  STORNIERT: 'Storniert',
  ZUGESTELLT: 'Zugestellt',
};

/** CSS-Modifier für das Status-Badge (`k-belegkarte__badge--<klasse>`). */
export function statusKlasse(status: string): string {
  return status.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

/** Lesbarer Status (je Belegart, wo die Fachsprache abweicht); unbekannte Werte
 *  werden aus dem Enum-Namen abgeleitet. */
export function statusLabel(status: string, typ?: BelegKarteTyp): string {
  if (typ === 'lieferschein' && status === 'VERSENDET') return 'Gebucht';
  if (typ === 'zahlung' && status === 'GENEHMIGT') return 'Gebucht';
  if (typ === 'zahlung' && status === 'ABGELEHNT') return 'Verworfen';
  if (STATUS_LABEL[status]) return STATUS_LABEL[status];
  const klein = status.toLowerCase().replace(/_/g, ' ');
  return klein.charAt(0).toUpperCase() + klein.slice(1);
}

const EURO = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

/** Betrag de-DE mit Euro-Zeichen; fehlende Werte als Gedankenstrich. */
export function formatBetrag(wert: number | null | undefined): string {
  return wert === null || wert === undefined || Number.isNaN(wert) ? '–' : EURO.format(wert);
}

/** Zeitstempel ohne Zonenangabe stammen aus `datetime.utcnow()` und sind UTC. */
export function parseZeit(iso: string): Date {
  // Reine Datumsstrings (2026-01-01) ohne Uhrzeit unveraendert parsen ("…Z" waere in Safari ungueltig).
  if (!/T\d/.test(iso)) return new Date(iso);
  const hatZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(iso);
  return new Date(hatZone ? iso : `${iso}Z`);
}

/** Relative Zeitangabe ("vor 10 Min."); älter als eine Woche als Datum. */
export function relativeZeit(iso: string | null | undefined, jetzt: Date = new Date()): string {
  if (!iso) return '';
  const zeit = parseZeit(iso);
  if (Number.isNaN(zeit.getTime())) return '';
  const minuten = Math.floor((jetzt.getTime() - zeit.getTime()) / 60000);
  if (minuten < 1) return 'gerade eben';
  if (minuten < 60) return `vor ${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  if (stunden < 24) return `vor ${stunden} Std.`;
  const tage = Math.floor(stunden / 24);
  if (tage < 7) return tage === 1 ? 'vor 1 Tag' : `vor ${tage} Tagen`;
  return `am ${zeit.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
}

/**
 * Fußzeile der Karte: "vom Agenten angelegt, vor x Min." nur, wenn der Server die Karte
 * als Agenten-Beleg kennzeichnet (herkunft.quelle === 'agent'); sonst ohne Urheber
 * "angelegt, vor x Min." (von Menschen angelegt oder Herkunft unbekannt).
 */
export function herkunftText(k: BelegKarte, jetzt: Date = new Date()): string {
  const wann = relativeZeit(k.herkunft?.erstellt_am, jetzt);
  let was = 'angelegt';
  if (k.herkunft?.quelle === 'agent') {
    const agent = k.herkunft.agent;
    was = `${agent && agent !== 'System' ? `vom ${agent}` : 'vom Agenten'} angelegt`;
  }
  return wann ? `${was}, ${wann}` : was;
}

/** Sicherheitsfrage vor "Festschreiben (ohne Mail)" (Rechnungsliste und Auftragsakte). */
export const FESTSCHREIBEN_FRAGE =
  'Rechnung festschreiben? Sie erhält jetzt ihre Nummer und kann nicht mehr bearbeitet werden.';

/** Fehlermeldung einer Axios-Antwort: detail als Text oder detail.message, sonst Fallback. */
export function fehlerText(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail) return detail;
  const message = (detail as { message?: unknown } | null | undefined)?.message;
  return typeof message === 'string' && message ? message : fallback;
}

/** Auftragszeile "Nummer · Kommission" oder null ohne Auftrag. */
export function auftragZeile(k: BelegKarte): string | null {
  if (!k.auftrag) return null;
  return k.auftrag.kommission ? `${k.auftrag.auftrag_nr} · ${k.auftrag.kommission}` : k.auftrag.auftrag_nr;
}
